import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Undo2 } from "lucide-react";
import { formatMoney } from "@/lib/money";
import { ConfirmActionDialog } from "@/components/ConfirmActionDialog";
import { toast } from "sonner";

interface Tx {
  id: string;
  sender_id: string;
  receiver_id: string;
  amount_cents: number;
  status: string;
  note: string | null;
  created_at: string;
}

export default function AdminTransactions() {
  const [rows, setRows] = useState<Tx[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [reversing, setReversing] = useState<Tx | null>(null);
  const [reversedIds, setReversedIds] = useState<Set<string>>(new Set());

  const load = async () => {
    setLoading(true);
    const [{ data }, { data: revs }] = await Promise.all([
      supabase
        .from("transactions")
        .select("id, sender_id, receiver_id, amount_cents, status, note, created_at")
        .order("created_at", { ascending: false })
        .limit(200),
      supabase.from("transaction_reversals").select("original_tx_id"),
    ]);
    setRows((data ?? []) as Tx[]);
    setReversedIds(new Set((revs ?? []).map((r: { original_tx_id: string }) => r.original_tx_id)));
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = filter
    ? rows.filter(
        (r) =>
          r.id.includes(filter) ||
          r.sender_id.includes(filter) ||
          r.receiver_id.includes(filter),
      )
    : rows;

  const doReverse = async (reason?: string) => {
    if (!reversing || !reason) return;
    const { error } = await supabase.rpc("admin_reverse_transaction", {
      _tx_id: reversing.id,
      _reason: reason,
    });
    if (error) {
      toast.error(error.message);
      throw error;
    }
    toast.success("Transaction reversed");
    load();
  };

  return (
    <div className="p-6">
      <div className="mb-5">
        <h1 className="font-display text-2xl font-bold">Transactions</h1>
        <p className="text-sm text-muted-foreground">Most recent 200 transactions.</p>
      </div>
      <Input
        className="max-w-md mb-4"
        placeholder="Filter by tx id or user id"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      />
      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((r) => {
            const isReversed = reversedIds.has(r.id);
            return (
              <Card key={r.id} className="p-3 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-mono text-xs text-muted-foreground truncate">{r.id}</span>
                  <div className="flex items-center gap-2">
                    {isReversed && <Badge variant="destructive">reversed</Badge>}
                    <Badge variant={r.status === "completed" ? "secondary" : "outline"}>
                      {r.status}
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="font-semibold">{formatMoney(r.amount_cents)}</span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(r.created_at).toLocaleString()}
                  </span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1 font-mono truncate">
                  {r.sender_id} → {r.receiver_id}
                </p>
                {r.note && <p className="text-xs italic mt-1">"{r.note}"</p>}
                {r.status === "completed" && !isReversed && (
                  <div className="mt-2 flex justify-end">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setReversing(r)}
                      className="text-xs"
                    >
                      <Undo2 className="h-3 w-3" /> Reverse
                    </Button>
                  </div>
                )}
              </Card>
            );
          })}
          {filtered.length === 0 && <p className="text-sm text-muted-foreground">No matches.</p>}
        </div>
      )}

      <ConfirmActionDialog
        open={!!reversing}
        onOpenChange={(o) => !o && setReversing(null)}
        title="Reverse transaction"
        description={
          reversing
            ? `Move ${formatMoney(reversing.amount_cents)} back from receiver to sender. Recipient must have sufficient balance.`
            : ""
        }
        onConfirm={doReverse}
        confirmLabel="Reverse"
      />
    </div>
  );
}
